import React, { ReactElement } from 'react'
import styles from '@/components/ExamChangeGuidePage/ExamChangeInfo/ExamChangeInfoSharedStyling.module.scss'
import { reusableCssClass } from '@/utils/reusableCssClasses'

export const Fall2024: React.FC = (): ReactElement => {
    return (
        <div className={styles.examChangeGuideSharedComponent} id={'fall2024'}>
            <div className={styles.examChangeGuidePageTop}>
                <h2>Fall 2024</h2>
                <h3>Summary</h3>
                <div>
                    The SOA has released changes effective for the Fall 2024
                    sitting of FSA Group and Health Exams. The updated syllabi
                    can be found here:
                    <ul>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/49955d/globalassets/assets/files/edu/2024/fall/syllabi/ghdp.pdf'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                Design and Pricing
                            </a>
                        </li>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/49955d/globalassets/assets/files/edu/2024/fall/syllabi/ghvru.pdf'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                Valuation and Regulation
                            </a>
                        </li>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/49964f/globalassets/assets/files/edu/2024/fall/syllabi/ghrm.pdf'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                Risk Mitigation
                            </a>{' '}
                            (Unchanged from Spring 2024)
                        </li>
                    </ul>
                    Overall, there were only minor changes to the syllabi, and
                    the general trend is that GHDP has one additional section
                    (which largely came from GHVR), while GHVR has less content
                    than before. The Retiree Benefits content has been moved
                    from GHVR to GHDP. This is a fairly small section overall,
                    but has been tested fairly regularly over the past few
                    years.
                    <br />
                    <br />
                    GHDP has the following changes:
                    <ul>
                        <li>
                            <b>Added</b> - Retiree Health Benefits - Accounting
                            and Valuation Assumptions. This section largely came
                            from the prior sitting&apos;s GHVR syllabus
                            (specifically GHVR-816-16). This material has been
                            tested quite frequently in the past (Q5 of GHVR Fall
                            2023, Q6 of GHVR Spring 2023, Q9 of GHVR Spring
                            2022, Q5 of GHFVCU Fall 2021 just to go back a few
                            years). However, certain notable sections were
                            omitted during the transition, namely the concept of
                            Funded Status and the computation of Net Periodic
                            Postretirement Benefit Cost (NPPBC), as well as
                            change in APBO over a year. Students should still
                            pay close attention to this section and expect it to
                            continue to be tested in the next sitting. It&apos;s
                            also worth pointing out there is an additional{' '}
                            <a
                                href={
                                    'https://www.soa.org/globalassets/assets/files/edu/2024/spring/case-study/ghdp-145-24.xlsx'
                                }
                                className={
                                    reusableCssClass.clickableTextUnderlined
                                }
                            >
                                Excel case study
                            </a>
                            , which is brand new to the group and health syllabi
                            and should also be carefully studied in order to be
                            fully prepared.
                        </li>
                    </ul>
                    GHVR has the following changes:
                    <ul>
                        <li>
                            <b>Trivial Renaming</b> - Chapter 6 of Individual
                            Health Insurance was changed to a study note and
                            renamed to GHVR 835-24. This is plausibly due to the
                            removal of the other chapters from this textbook
                            from the syllabus.
                        </li>
                        <li>
                            <b>Removed</b> - Chapter 8 of Individual Health
                            Insurance was completely removed from the syllabus.
                            This section was tested only once in the past (Q3 of
                            GHVR Spring 2023).
                        </li>
                        <li>
                            <b>Trivial Renaming</b> -{' '}
                            <a
                                href={
                                    'https://www.soa.org/globalassets/assets/library/newsletters/health-section-news/2004/october/hsn-2004-iss48-DobsonSnook.pdf'
                                }
                                className={
                                    reusableCssClass.clickableTextUnderlined
                                }
                            >
                                Read. Think. Write.
                            </a>{' '}
                            was simply moved to a different part of the syllabus
                            (same exam).
                        </li>
                        <li>
                            <b>Removed</b> - GHVR 830-21: A Hard Pill to
                            Swallow: Appreciating the Mathematical Dynamics of
                            the Affordable Care Act was completely removed from
                            the syllabus. This section was tested only once in
                            the past (Q6c of the GHFVCU Fall 2021 sitting).
                        </li>
                        <li>
                            <b>Removed</b> - Group Insurance Chapter 8: Retiree
                            Group Benefits. This section was removed from this
                            exam. This material had been tested quite frequently
                            in the past (Q10 of GHFVU Fall 2022, Q5a of GHFVCU
                            Fall 2021, Q2 of GHFVCU Spring 2021, just to go back
                            a few years).
                        </li>
                        <li>
                            <b>Removed</b> - GHVR 816-16: US Employers&apos;
                            Accounting of Postretirement Benefits Other Than
                            Pensions Study Note was completely removed from the
                            syllabus. This material had been tested quite
                            frequently in the past (Q5 of GHVR Fall 2023, Q6 of
                            GHVR Spring 2023, Q9 of GHVR Spring 2022, Q5 of
                            GHFVCU Fall 2021 just to go back a few years).
                        </li>
                    </ul>
                </div>
                <p>GHRM has no syllabus changes.</p>
            </div>
        </div>
    )
}
